import { unByKey } from 'ol/Observable';
import { useSelectionStore, SelectedFeature } from '@/stores/selectionStore';
import { MapInteractionMode, useMapStore } from '@/stores/mapStore';
import { useHighlightLayer } from './useHighlightLayer';
import { LayerDefinition, SystemLayerKey } from '@packages/database/types';
import LayerGroup from 'ol/layer/Group';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { useCallback, useEffect } from 'react';
import { Feature, Map as OlMap } from 'ol';

interface Props {
  map: OlMap | null;
}

export const useMapClickHandler = ({ map }: Props) => {
  const { interactionMode, layers } = useMapStore();
  const { setSelection, setMarker, clear, setProjectArea } =
    useSelectionStore();

  const { clearHighlight, toggleHighlight } = useHighlightLayer();

  const getLayerSource = useCallback(
    (layerId: string): VectorSource | null => {
      if (!map) return null;
      let source: VectorSource | null = null;

      const search = (collection: any) => {
        collection.forEach((l: any) => {
          if (l instanceof LayerGroup) {
            search(l.getLayers());
          } else if (
            l.get('config')?.id === layerId &&
            l instanceof VectorLayer
          ) {
            source = l.getSource();
          }
        });
      };

      search(map.getLayers());
      return source;
    },
    [map],
  );

  const handleViewClick = useCallback(
    (evt: any) => {
      if (!map) return;

      const buckets = {
        points: [] as SelectedFeature[],
        lines: [] as SelectedFeature[],
        polygons: [] as SelectedFeature[],
      };

      const seen = new Set<string>();

      map.forEachFeatureAtPixel(
        evt.pixel,
        (feature, olLayer) => {
          if (olLayer?.get('type') === 'highlight') return;

          const id = feature.getId()?.toString();
          const config = olLayer?.get('config') as LayerDefinition;
          if (!id || !config || seen.has(id)) return;
          seen.add(id);

          const item: SelectedFeature = {
            id,
            layerKey: config.key,
            layer: config,
            data: feature.getProperties(),
            feature: feature as any,
            geometry: feature.getGeometry(),
            coordinate: evt.coordinate,
          };

          const geom = feature.getGeometry()?.getType();
          if (geom === 'Point' || geom === 'MultiPoint') {
            buckets.points.push(item);
          } else if (geom === 'LineString' || geom === 'MultiLineString') {
            buckets.lines.push(item);
          } else {
            buckets.polygons.push(item);
          }
        },
        { hitTolerance: 5 },
      );

      const selectedItems =
        buckets.points.length > 0
          ? buckets.points
          : buckets.lines.length > 0
            ? buckets.lines
            : buckets.polygons.length > 0
              ? buckets.polygons
              : [];

      if (selectedItems.length > 0) {
        setSelection(selectedItems, 'map', evt.coordinate);
      } else {
        clear();
      }

      // if (candidates.length === 0) {
      //   clearSelection();
      //   return;
      // }

      // if (candidates.length === 1) {
      //   // One call — config drives everything from here
      //   selectFeature(candidates[0], 'map');
      //   return;
      // }

      // setCandidates(candidates, evt.coordinate);
    },
    [map, setSelection, clear],
  );

  const handlePointClick = useCallback(
    (evt: any) => {
      setMarker({ coordinate: evt.coordinate, data: null });
    },
    [setMarker],
  );

  const handleProjectAreaClick = useCallback(
    (evt: any) => {
      if (!map) return;

      const serviceAreaFeature = map.getFeaturesAtPixel(evt.pixel, {
        layerFilter: (l) =>
          l.get('config')?.key === SystemLayerKey.SERVICE_AREA,
      })[0];

      clear();
      clearHighlight();

      if (!serviceAreaFeature) {
        setProjectArea(null);
        return;
      }

      const geometry = serviceAreaFeature.getGeometry();
      const extent = geometry?.getExtent();
      const boundaryId = serviceAreaFeature.getId()?.toString();
      if (!extent || !boundaryId) return;

      clear();
      toggleHighlight(serviceAreaFeature as Feature);

      const selectableLayers = layers.filter(
        (l) =>
          (l.sourceTable === 'assets' || l.sourceTable === 'premises') &&
          l.isVisible,
      );

      const found = new Map<string, any>();

      selectableLayers.forEach((layerDef) => {
        const source = getLayerSource(layerDef.id);
        if (!source) return;

        source.forEachFeatureIntersectingExtent(extent, (feature) => {
          const id = feature.getId()?.toString() || feature.get('id');
          if (!id || found.has(id)) return;

          found.set(id, {
            id,
            layerKey: layerDef.key,
            sourceTable: layerDef.sourceTable,
            properties: feature.getProperties(),
          });

          toggleHighlight(feature as Feature);
        });
      });

      setProjectArea({
        serviceAreaId: boundaryId,
        boundaryExtent: extent,
        features: Array.from(found.values()),
      });
    },
    [map, layers, clear, toggleHighlight, setProjectArea, getLayerSource],
  );

  useEffect(() => {
    if (!map) return;

    const target = map.getTargetElement();

    const CURSOR_MAP: Partial<Record<MapInteractionMode, string>> = {
      view: 'default',
      addNote: 'crosshair',
      addFieldCorrection: 'crosshair',
      selectProjectArea: 'pointer',
      redlining: 'crosshair',
    };

    const HANDLER_MAP: Partial<Record<MapInteractionMode, (evt: any) => void>> =
      {
        view: handleViewClick,
        addNote: handlePointClick,
        addFieldCorrection: handlePointClick,
        selectProjectArea: handleProjectAreaClick,
      };

    target.style.cursor = CURSOR_MAP[interactionMode] ?? 'default';

    const handler = HANDLER_MAP[interactionMode];
    if (!handler) return;

    const key = map.on('singleclick', handler);

    return () => {
      unByKey(key);
      target.style.cursor = 'default';
    };
  }, [
    map,
    interactionMode,
    handleViewClick,
    handlePointClick,
    handleProjectAreaClick,
  ]);
};
